import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { formatCartonPerDaysLabel } from '@theme/nro-labels';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier, restoreStashedSelectOptions, detachHiddenSelectOptions } from '@theme/utilities';

const purchaseOptionsTransitionHandlers = new WeakMap();
const PURCHASE_OPTIONS_CLOSE_DURATION = 'calc(var(--animation-speed) * 1.75)';

const USAGE_HARD_CEILING = 12;
/** PDP-02: how long a prefetched variant section stays usable. */
const PREFETCH_TTL_MS = 60_000;
/** PDP-02: pickers with more unselected options than this prefetch on intent only, never all at once. */
const MAX_IDLE_PREFETCH = 3;
// Linked products (the Color and Absorbency rows): links to separate products, swapped in place on the product page
const LINKED_PRODUCT_LINK = '.variant-option--linked-products a[href]';

/**
 * Decide whether a given (usage, planDays) combination would produce a visible
 * subscription option. Mirrors the filter logic in #updatePurchaseOptionsLabels
 * so the usage dropdown and the plan dropdown stay in sync.
 *
 * @param {number} usage
 * @param {number} planDays
 * @param {number} piecesPerVariant
 * @param {number} nroMinPieces - 0 means no minimum
 * @param {number} nroMaxPacks - 0 means no maximum
 * @param {number} toleranceDays
 * @returns {boolean}
 */
function usageProducesVisiblePlan(usage, planDays, piecesPerVariant, nroMinPieces, nroMaxPacks, nroMaxCartonCount, toleranceDays) {
  if (piecesPerVariant <= 0 || !Number.isFinite(planDays) || planDays <= 0) return false;
  const cartonCount = Math.max(1, Math.round((planDays * usage) / piecesPerVariant));
  const expectedPlanDays = (cartonCount * piecesPerVariant) / usage;
  if (Math.abs(planDays - expectedPlanDays) > toleranceDays) return false;
  if (nroMinPieces > 0 && cartonCount * piecesPerVariant < nroMinPieces) return false;
  if (nroMaxPacks > 0 && cartonCount > nroMaxPacks) return false;
  if (nroMaxCartonCount > 0 && cartonCount > nroMaxCartonCount) return false;
  return true;
}

/**
 * Compute the valid [minUsage, maxUsage] range — usage values where at least
 * one of planDaysValues passes the filter.
 *
 * @param {object} cfg
 * @returns {{ minUsage: number, maxUsage: number }}
 */
function computeUsageRange(cfg) {
  const {
    piecesPerVariant,
    planDaysValues,
    nroMinPieces,
    nroMaxPacks,
    nroMaxCartonCount,
    maxUsage: configuredMaxUsage,
    toleranceDays,
  } = cfg;
  const usageCeiling = Number.isFinite(configuredMaxUsage) && configuredMaxUsage > 0
    ? Math.floor(configuredMaxUsage)
    : USAGE_HARD_CEILING;

  if (!planDaysValues || planDaysValues.length === 0 || piecesPerVariant <= 0) {
    return { minUsage: 1, maxUsage: usageCeiling };
  }

  let minUsage = 0;
  let maxUsage = 0;
  for (let u = 1; u <= usageCeiling; u++) {
    const fits = planDaysValues.some((d) =>
      usageProducesVisiblePlan(u, d, piecesPerVariant, nroMinPieces, nroMaxPacks, nroMaxCartonCount, toleranceDays)
    );
    if (fits) {
      if (minUsage === 0) minUsage = u;
      maxUsage = u;
    }
  }
  if (minUsage === 0) {
    // Nothing fits — keep dropdown at 1 so it still has a valid option,
    // but downstream code will hide all plans anyway.
    return { minUsage: 1, maxUsage: 1 };
  }
  return { minUsage, maxUsage };
}

/**
 * Hide/show options in a usage <select> so only values in the valid range
 * are pickable. Preserves the user's current selection when still valid;
 * otherwise snaps to the nearest valid value.
 *
 * @param {HTMLSelectElement} usageSelect
 * @param {object} cfg
 */
function syncPurchaseOptionsUsageRange(usageSelect, cfg) {
  restoreStashedSelectOptions(usageSelect);
  const { minUsage, maxUsage } = computeUsageRange(cfg);
  let anyVisible = false;
  let firstVisible = null;
  for (const option of usageSelect.options) {
    const value = Number.parseInt(option.value || '0', 10);
    const inRange = value >= minUsage && value <= maxUsage;
    option.hidden = !inRange;
    option.disabled = !inRange;
    if (inRange) {
      anyVisible = true;
      if (firstVisible == null) firstVisible = String(value);
    }
  }
  detachHiddenSelectOptions(usageSelect);
  if (!anyVisible) return;
  const currentValue = Number.parseInt(usageSelect.value || '0', 10);
  if (currentValue < minUsage || currentValue > maxUsage) {
    // Snap to nearest valid (prefer current direction).
    if (currentValue < minUsage) usageSelect.value = String(minUsage);
    else usageSelect.value = String(maxUsage);
  } else if (!usageSelect.value && firstVisible != null) {
    usageSelect.value = firstVisible;
  }
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | undefined} */
  #activeRequestUrl;

  /** @type {string | undefined} */
  #lastCompletedRequestUrl;

  /** @type {Promise<void> | null} */
  #pendingPurchaseOptionsClose = null;

  /** @type {number} */
  #requestToken = 0;

  /**
   * PDP-02: section HTML already fetched (or on its way) for a variant's request URL, so
   * tapping a prefetched option needs no network. Cleared whenever the cart changes.
   * @type {Map<string, { at: number, text: Promise<string> }>}
   */
  #prefetched = new Map();

  /** @type {AbortController | null} */
  #prefetchListeners = null;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** @type {HTMLElement | null} */
  #quantityEventTarget = null;

  #onQuantitySelectorUpdate = (event) => {
    const form = this.#getProductFormContainer();
    if (!(form instanceof HTMLElement)) return;
    if (!(event.target instanceof Node)) return;
    if (!form.contains(event.target)) return;

    this.#updateAddToCartTotalPrice();
  };

  #captureSelectionSnapshot() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const snapshotByFieldset = new Map();

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset?.querySelectorAll('input[data-option-value-id]') ?? []);
      const checkedRadio = radios.find((radio) => radio.checked);
      if (!checkedRadio) return;

      const rawInputIndex = Number.parseInt(checkedRadio.dataset.inputIndex || '', 10);
      snapshotByFieldset.set(fieldsetIndex, {
        inputId: checkedRadio.dataset.inputId || '',
        inputIndex: Number.isNaN(rawInputIndex) ? -1 : rawInputIndex,
        renderedIndex: radios.indexOf(checkedRadio),
      });
    });

    return snapshotByFieldset;
  }

  #pickFallbackRadio(radios, fieldsetIndex, selectionSnapshot) {
    const selectableRadios = radios.filter(
      (radio) => radio.dataset.optionAvailable !== 'false' && radio.getAttribute('aria-disabled') !== 'true'
    );
    if (!selectableRadios.length) return undefined;
    if (!selectionSnapshot) return selectableRadios[0];

    const previousSelection = selectionSnapshot.get(fieldsetIndex);
    if (!previousSelection) return selectableRadios[0];

    const sameInputIdRadio = selectableRadios.find((radio) => radio.dataset.inputId === previousSelection.inputId);
    if (sameInputIdRadio) return sameInputIdRadio;

    const scoredOptions = selectableRadios.map((radio) => {
      const rawInputIndex = Number.parseInt(radio.dataset.inputIndex || '', 10);
      const inputIndex = Number.isNaN(rawInputIndex) ? Number.MAX_SAFE_INTEGER : rawInputIndex;
      const renderedIndex = radios.indexOf(radio);
      const inputDistance =
        previousSelection.inputIndex >= 0 ? Math.abs(inputIndex - previousSelection.inputIndex) : Number.MAX_SAFE_INTEGER;
      const renderedDistance =
        previousSelection.renderedIndex >= 0
          ? Math.abs(renderedIndex - previousSelection.renderedIndex)
          : Number.MAX_SAFE_INTEGER;

      return {
        radio,
        inputDistance,
        renderedDistance,
      };
    });

    scoredOptions.sort((a, b) => {
      if (a.inputDistance !== b.inputDistance) return a.inputDistance - b.inputDistance;
      return a.renderedDistance - b.renderedDistance;
    });

    return scoredOptions[0]?.radio || selectableRadios[0];
  }

  #refreshVariantInputsState({ autoSelectFallback = false, selectionSnapshot } = {}) {
    this.#checkedIndices = [];
    this.#radios = [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    /** @type {HTMLInputElement | undefined} */
    let fallbackInput;

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset?.querySelectorAll('input[data-option-value-id]') ?? []);
      this.#radios.push(radios);

      if (!radios.length) {
        this.#checkedIndices.push([]);
        return;
      }

      const checkedRadios = radios.filter((radio) => radio.checked);
      let activeRadio = checkedRadios.find((radio) => radio.dataset.currentChecked === 'true') || checkedRadios[0];

      if (!activeRadio && autoSelectFallback) {
        activeRadio = this.#pickFallbackRadio(radios, fieldsetIndex, selectionSnapshot);
        if (activeRadio) {
          activeRadio.checked = true;
          if (!fallbackInput) {
            fallbackInput = activeRadio;
          }
        }
      }

      radios.forEach((radio) => {
        const isActive = radio === activeRadio;
        radio.dataset.currentChecked = isActive ? 'true' : 'false';
        if (!isActive) {
          radio.dataset.previousChecked = 'false';
          radio.checked = false;
        }
      });

      const activeIndex = activeRadio ? radios.indexOf(activeRadio) : -1;
      if (activeIndex !== -1) {
        this.#checkedIndices.push([activeIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });

    return fallbackInput;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#refreshVariantInputsState();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('change', this.#handlePurchaseOptionsChange);
    this.#resizeObserver.observe(this);
    this.#initPurchaseOptions();
    // QuantitySelectorUpdateEvent bubbles to document. We listen there so we
    // catch +/- clicks from the buy-buttons quantity selector, which lives
    // OUTSIDE the variant-picker subtree. (#onQuantitySelectorUpdate filters
    // by form ownership so other product cards' qty changes don't bleed in.)
    this.#quantityEventTarget = document;
    this.#quantityEventTarget.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    this.#updateAddToCartTotalPrice();
    this.#listenForPrefetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#prefetchListeners?.abort();
    this.#prefetchListeners = null;
    this.#quantityEventTarget?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    this.#quantityEventTarget = null;
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;
    if (!selectedOption.dataset.optionValueId) return;

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const requestUrl = this.buildRequestUrl(selectedOption);
    if (requestUrl === this.#activeRequestUrl) {
      return;
    }

    this.#requestToken += 1;
    const requestToken = this.#requestToken;
    this.#pendingPurchaseOptionsClose = this.#closePurchaseOptionsForTransition();
    this.fetchUpdatedSection(
      requestUrl,
      morphElementSelector,
      requestToken,
      selectedOption.dataset.optionValueId ?? ''
    );

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    if (!source && this.#fetchesMainSection(selectedOption)) {
      return `${productUrl?.split('?')[0]}?section_id=${this.dataset.sectionId}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * PDP-02: on the product page a variant change only needs the main product section,
   * about a quarter of the page, and every component that updates from the response
   * lives inside it. An option that links to another product (a combined listing) still
   * fetches the whole page, because that morphs `main`.
   * @param {HTMLElement} [option]
   * @returns {boolean}
   */
  #fetchesMainSection(option) {
    if (this.dataset.templateProductMatch !== 'true' || !this.dataset.sectionId) return false;
    if (this.closest('product-card, quick-add-dialog, quick-add-component, featured-product-information')) return false;
    const connectedUrl = option?.dataset.connectedProductUrl;
    return !connectedUrl || connectedUrl === this.dataset.productUrl?.split('?')[0];
  }

  /**
   * The URL buildRequestUrl() would produce once `option` is selected, without selecting it.
   * @param {HTMLInputElement} option
   * @returns {string | null}
   */
  #requestUrlIfSelected(option) {
    const fieldset = option.closest('fieldset');
    const checked = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('select option[selected][data-option-value-id], fieldset input[data-option-value-id]:checked'),
    ]);
    if (!fieldset || !checked.some((selected) => selected.closest('fieldset') === fieldset)) return null;
    const values = checked.map((selected) => (selected.closest('fieldset') === fieldset ? option : selected).dataset.optionValueId);
    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    params.push(`option_values=${values.join(',')}`);
    return `${this.dataset.productUrl?.split('?')[0]}?section_id=${this.dataset.sectionId}&${params.join('&')}`;
  }

  /** @param {HTMLInputElement} option */
  #prefetch(option) {
    if (option.checked || !option.dataset.optionValueId || !this.#fetchesMainSection(option)) return;
    const url = this.#requestUrlIfSelected(option);
    if (url) this.#prefetchUrl(url);
  }

  /** @param {HTMLAnchorElement} link */
  #prefetchLinked(link) {
    const url = this.#linkedRequestUrl(link);
    if (url) this.#prefetchUrl(url);
  }

  /** @param {string} url */
  #prefetchUrl(url) {
    if (this.#freshPrefetch(url)) return;
    const text = fetch(url).then((response) => (response.ok ? response.text() : Promise.reject(new Error(`${response.status}`))));
    text.catch(() => this.#prefetched.delete(url));
    this.#prefetched.set(url, { at: performance.now(), text });
  }

  /**
   * The same-origin path and query a linked-product link loads, or null for the current product.
   * @param {HTMLAnchorElement} link
   * @returns {string | null}
   */
  #linkedRequestUrl(link) {
    if (link.getAttribute('aria-current') === 'page') return null;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}`;
  }

  /**
   * Linked products are separate products. On the product page a click swaps the other product in place, as
   * Shopify's combined listings do: fetch its page (usually prefetched), morph `main`, then update the URL and
   * title in place, so Back still leaves the product. New-tab clicks are left to the browser, and a failed swap
   * follows the link.
   * @param {MouseEvent} event
   */
  #onLinkedProductClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const link = event.target instanceof Element ? event.target.closest(LINKED_PRODUCT_LINK) : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    if (link.getAttribute('aria-current') === 'page') return;
    const requestUrl = this.#linkedRequestUrl(link);
    if (requestUrl) this.#loadLinkedProduct(link, requestUrl);
    else window.location.assign(link.href);
  };

  /**
   * @param {HTMLAnchorElement} link
   * @param {string} requestUrl
   */
  async #loadLinkedProduct(link, requestUrl) {
    // Already on its way (a second tap on the same link)
    if (requestUrl === this.#activeRequestUrl) return;
    this.#requestToken += 1;
    const requestToken = this.#requestToken;
    const fieldset = link.closest('fieldset');
    const fieldsetIndex = fieldset?.dataset.fieldsetIndex;
    const hadFocus = document.activeElement === link;

    // Show the choice straight away; the swapped-in page confirms it
    for (const selected of fieldset?.querySelectorAll('a.is-selected') ?? []) selected.classList.remove('is-selected');
    link.classList.add('is-selected');
    fieldset?.setAttribute('aria-busy', 'true');

    const html = await this.fetchUpdatedSection(requestUrl, 'main', requestToken, '');
    if (requestToken !== this.#requestToken) return;
    if (!html) {
      window.location.assign(link.href);
      return;
    }

    if (html.title) document.title = html.title;
    yieldToMainThread().then(() => history.replaceState(history.state, '', requestUrl));
    // The new product's own options and linked products, ready for the next tap
    this.#prefetchOthersWhenIdle();
    // The new product's app blocks (Okendo reviews) arrive inert, like on a page load: watch them so they load near
    // the viewport, and let Okendo pick up anything already in place
    const main = document.querySelector('main');
    if (main) {
      import('@theme/lazy-deferred-section')
        .then(({ watchLazyDeferredContent }) => watchLazyDeferredContent(main))
        .catch(() => {});
    }
    /** @type {any} */ (window).okeWidgetApi?.initAllWidgets?.();

    if (hadFocus && fieldsetIndex) {
      const current = document.querySelector(
        `variant-picker fieldset[data-fieldset-index="${CSS.escape(fieldsetIndex)}"] a[aria-current="page"]`
      );
      if (current instanceof HTMLElement) current.focus({ preventScroll: true });
    }
  }

  /**
   * @param {string} url
   * @returns {Promise<string> | undefined}
   */
  #freshPrefetch(url) {
    const entry = this.#prefetched.get(url);
    if (!entry) return undefined;
    if (performance.now() - entry.at < PREFETCH_TTL_MS) return entry.text;
    this.#prefetched.delete(url);
    return undefined;
  }

  /**
   * PDP-02: prefetch an option's section on the first sign of intent (hover, touch,
   * focus), and in the full tier the other options once the page is idle.
   */
  #listenForPrefetch() {
    this.#prefetchListeners?.abort();
    this.#prefetchListeners = null;
    if (!this.#fetchesMainSection()) return;
    const controller = new AbortController();
    this.#prefetchListeners = controller;
    const { signal } = controller;
    const onIntent = (/** @type {Event} */ event) => {
      const linked = event.target instanceof Element ? event.target.closest(LINKED_PRODUCT_LINK) : null;
      if (linked instanceof HTMLAnchorElement) {
        this.#prefetchLinked(linked);
        return;
      }
      const target = event.target instanceof Element ? event.target.closest('label, input') : null;
      const input = target instanceof HTMLInputElement ? target : target?.querySelector('input[data-option-value-id]');
      if (input instanceof HTMLInputElement) this.#prefetch(input);
    };
    this.addEventListener('pointerover', onIntent, { signal, passive: true });
    this.addEventListener('touchstart', onIntent, { signal, passive: true });
    this.addEventListener('focusin', onIntent, { signal });
    this.addEventListener('click', this.#onLinkedProductClick, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, () => this.#prefetched.clear(), { signal });
    this.#prefetchOthersWhenIdle();
  }

  #prefetchOthersWhenIdle() {
    if (window.__perf?.tier === 'lite' || /** @type {any} */ (navigator).connection?.saveData) return;
    const run = () => {
      if (!this.isConnected) return;
      const others = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('fieldset input[data-option-value-id]:not(:checked)')]);
      if (others.length <= MAX_IDLE_PREFETCH) for (const option of others) this.#prefetch(option);
      // The other linked products' pages (whole pages, about 100 KB each), so a Color or Absorbency switch is instant.
      // Only on a fast connection; elsewhere they're fetched on hover, touch or focus.
      const effectiveType = /** @type {any} */ (navigator).connection?.effectiveType;
      if (effectiveType && effectiveType !== '4g') return;
      const linked = /** @type {HTMLAnchorElement[]} */ ([...this.querySelectorAll(`${LINKED_PRODUCT_LINK}:not([aria-current="page"])`)]);
      if (linked.length <= MAX_IDLE_PREFETCH) for (const link of linked) this.#prefetchLinked(link);
    };
    const whenIdle = () => ('requestIdleCallback' in window ? requestIdleCallback(run, { timeout: 4000 }) : setTimeout(run, 1500));
    if (document.readyState === 'complete') whenIdle();
    else window.addEventListener('load', whenIdle, { once: true });
  }

  /**
   * @param {Document} html
   * @param {string} textContent
   * @param {NewProduct | undefined} newProduct
   */
  #emitVariantUpdateEvent(html, textContent, newProduct) {
    /** @type {object | null} */
    let variant = null;
    try {
      variant = JSON.parse(textContent);
    } catch (error) {
      console.warn('Invalid variant JSON in section response', error);
    }

    let sourceId = '';
    try {
      sourceId = this.selectedOptionId ?? '';
    } catch (error) {
      console.warn('Unable to resolve selected option id after variant update', error);
    }

    this.dispatchEvent(
      new VariantUpdateEvent(variant, sourceId, {
        html,
        productId: this.dataset.productId ?? '',
        newProduct,
      })
    );
  }

  /**
   * Clear loading UI when a variant fetch fails after the latest request token.
   * @param {number} requestToken
   */
  #emitVariantTransitionFailure(requestToken) {
    if (requestToken !== this.#requestToken) return;

    let sourceId = '';
    try {
      sourceId = this.selectedOptionId ?? '';
    } catch {
      sourceId = '';
    }

    this.dispatchEvent(
      new VariantUpdateEvent(null, sourceId, {
        html: document,
        productId: this.dataset.productId ?? '',
      })
    );
  }

  /**
   * @param {Document} html
   * @param {string} textContent
   * @param {string | undefined} morphElementSelector
   * @returns {NewProduct | undefined}
   */
  #applyVariantSectionResponse(html, textContent, morphElementSelector) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    if (morphElementSelector === 'main') {
      this.updateMain(html);
    } else if (morphElementSelector) {
      this.updateElement(html, morphElementSelector);
    } else {
      newProduct = this.updateVariantPicker(html);
    }

    this.#emitVariantUpdateEvent(html, textContent, newProduct);
    return newProduct;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {number} [requestToken] - The request token used to ignore stale responses.
   * @param {string} [selectedOptionValueId] - Option value id for variant:selected (loading UI).
   * @returns {Promise<Document | undefined> | undefined} The response once applied; undefined if skipped, stale or failed.
   */
  fetchUpdatedSection(
    requestUrl,
    morphElementSelector,
    requestToken = this.#requestToken,
    selectedOptionValueId = ''
  ) {
    // Skip if an identical request is already in-flight to avoid redundant network calls.
    if (requestUrl === this.#activeRequestUrl) {
      return;
    }

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#activeRequestUrl = requestUrl;
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOptionValueId }));

    const signal = this.#abortController.signal;
    const network = () => fetch(requestUrl, { signal }).then((response) => response.text());
    const prefetched = this.#freshPrefetch(requestUrl);

    return (prefetched ? prefetched.catch(network) : network())
      .then(async (responseText) => {
        // A prefetched response can't be aborted: if a newer tap has started, it wins.
        if (prefetched && requestToken !== this.#requestToken) return;
        this.#pendingRequestUrl = undefined;
        this.#activeRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(
          'variant-picker script[type="application/json"]:not([data-purchase-options-matrix])'
        )?.textContent;
        if (!textContent) {
          this.#emitVariantTransitionFailure(requestToken);
          return;
        }

        if (this.#pendingPurchaseOptionsClose) {
          await this.#pendingPurchaseOptionsClose;
          this.#pendingPurchaseOptionsClose = null;
        }

        try {
          this.#applyVariantSectionResponse(html, textContent, morphElementSelector);
          this.#lastCompletedRequestUrl = requestUrl;
          if (requestUrl.includes('section_id=') && this.#fetchesMainSection()) this.#prefetchOthersWhenIdle();
        } catch (error) {
          this.#emitVariantTransitionFailure(requestToken);
          throw error;
        }

        if (requestToken === this.#requestToken) {
          this.#openPurchaseOptionsAfterTransition();
        }
        return html;
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          this.#activeRequestUrl = undefined;
          console.error(error);
          this.#emitVariantTransitionFailure(requestToken);
          if (requestToken === this.#requestToken) {
            this.#openPurchaseOptionsAfterTransition();
          }
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;
    const selectionSnapshot = this.#captureSelectionSnapshot();

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    const fallbackInput = this.#refreshVariantInputsState({
      autoSelectFallback: true,
      selectionSnapshot,
    });
    this.updateVariantPickerCss();
    this.#initPurchaseOptions();
    this.#updateAddToCartTotalPrice();
    if (fallbackInput?.dataset.optionValueId) {
      queueMicrotask(() => {
        const requestUrl = this.buildRequestUrl(fallbackInput);
        if (requestUrl === this.#lastCompletedRequestUrl || requestUrl === this.#activeRequestUrl) {
          return;
        }

        fallbackInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    // Free-shipping callouts arrive in their server state (shown). Apply the show/hide rule to the incoming markup
    // first, with the cart total the page already knows, so the wrong callout never paints during the swap.
    const cartTotalCents = Number.parseInt(`${/** @type {any} */ (window).Theme?.cart?.totalPriceCents ?? ''}`, 10);
    if (Number.isFinite(cartTotalCents)) {
      document.dispatchEvent(
        new CustomEvent('free-shipping:context-update', { detail: { scope: newMain, cartTotalCents } })
      );
    }

    // The new product's selected options, as the server rendered them for its variant
    const checkedOptionIds = new Set(
      Array.from(newMain.querySelectorAll('variant-picker input[data-option-value-id]'))
        .filter((input) => input instanceof HTMLInputElement && input.checked)
        .map((input) => /** @type {HTMLInputElement} */ (input).dataset.optionValueId)
    );

    morph(main, newMain);

    // No cart total yet: let free-shipping-visibility.js look it up for the swapped-in callouts
    if (!Number.isFinite(cartTotalCents)) {
      document.dispatchEvent(new CustomEvent('free-shipping:context-update', { detail: { scope: main } }));
    }
    // Scripts that set their markup up once on load (the vertical slideshow) set the swapped-in content up again
    document.dispatchEvent(new CustomEvent('theme:main-swapped', { detail: { scope: main } }));

    // Another product is now in place (a linked product or a combined listing). The picker keeps its element, so
    // set its new options up as updateVariantPicker() does: input state, CSS, and the purchase options (the
    // Receive labels and filtering, discount, quantity), which otherwise show the raw selling plan names.
    if (!this.isConnected) return;
    // The morph reuses option inputs across products with different options (Maxi has no Style row, Maxi Plus has),
    // and can leave a reused radio checked (or with a stale checked attribute). Apply the server's selection exactly.
    for (const input of this.querySelectorAll('input[data-option-value-id]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const checked = checkedOptionIds.has(input.dataset.optionValueId);
      input.checked = checked;
      input.toggleAttribute('checked', checked);
    }
    // A row still without a choice takes its first available option, and that variant loads
    const fallbackInput = this.#refreshVariantInputsState({ autoSelectFallback: true });
    this.updateVariantPickerCss();
    this.#initPurchaseOptions();
    this.#updateAddToCartTotalPrice();
    if (fallbackInput?.dataset.optionValueId) {
      queueMicrotask(() => fallbackInput.dispatchEvent(new Event('change', { bubbles: true })));
    }
  }

  #handlePurchaseOptionsChange = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const fieldset = event.target.closest('[data-purchase-options]');
    if (!fieldset) return;

    if (event.target.matches('[data-purchase-options-usage]')) {
      const matrix = this.#getPurchaseOptionsMatrix();
      const planDaysMap = matrix ? this.#getPlanDaysMap(matrix) : new Map();
      this.#updatePurchaseOptionsLabels(fieldset, planDaysMap);
      const planSelect = fieldset.querySelector('[data-purchase-options-plan]');
      if (planSelect instanceof HTMLSelectElement) {
        const firstVisibleOption = Array.from(planSelect.options).find(
          (option) => !option.hidden && option.value !== ''
        );
        if (firstVisibleOption) {
          planSelect.value = firstVisibleOption.value;
        }
      }
      this.#updatePurchaseOptionsDiscount(fieldset, matrix);
      this.#syncPurchaseOptionsQuantity(fieldset);
      this.#updateAddToCartTotalPrice();
      return;
    }

    if (event.target.matches('[data-purchase-options-toggle]')) {
      this.#togglePurchaseOptions(fieldset, { animate: true });
      this.#syncPurchaseOptionsQuantity(fieldset);
      this.#updateAddToCartTotalPrice();
      return;
    }

    if (event.target.matches('[data-purchase-options-plan]')) {
      const matrix = this.#getPurchaseOptionsMatrix();
      this.#updatePurchaseOptionsDiscount(fieldset, matrix);
      this.#syncPurchaseOptionsQuantity(fieldset);
      this.#updateAddToCartTotalPrice();
    }
  };

  #initPurchaseOptions() {
    const fieldset = this.querySelector('[data-purchase-options]');
    if (!(fieldset instanceof HTMLElement)) return;

    const usageSelect = fieldset.querySelector('[data-purchase-options-usage]');
    if (usageSelect instanceof HTMLSelectElement && !usageSelect.value) {
      usageSelect.value = '1';
    }

    const planSelect = fieldset.querySelector('[data-purchase-options-plan]');
    if (!(planSelect instanceof HTMLSelectElement)) return;

    const currentSellingPlanId = fieldset.dataset.currentSellingPlanId;
    const requiresSellingPlan = fieldset.dataset.requiresSellingPlan === 'true';
    const hasSubscriptionOptions = fieldset.dataset.subscriptionEnabled === 'true';
    const toggle = fieldset.querySelector('[data-purchase-options-toggle]');
    if (toggle instanceof HTMLInputElement) {
      toggle.disabled = !hasSubscriptionOptions;
      if (!hasSubscriptionOptions) {
        toggle.checked = false;
      }
    }

    if (currentSellingPlanId) {
      planSelect.value = currentSellingPlanId;
    } else if (requiresSellingPlan) {
      const firstPlanOption = Array.from(planSelect.options).find((option) => option.value);
      if (firstPlanOption) {
        planSelect.value = firstPlanOption.value;
      }
    }

    const matrix = this.#getPurchaseOptionsMatrix();
    if (matrix) {
      const planDaysMap = this.#getPlanDaysMap(matrix);
      this.#updatePurchaseOptionsLabels(fieldset, planDaysMap);
    } else {
      this.#updatePurchaseOptionsLabels(fieldset, new Map());
    }

    this.#setPurchaseOptionsVisibility(fieldset, { animate: true });
    this.#togglePurchaseOptions(fieldset, { animate: true });
    this.#updatePurchaseOptionsDiscount(fieldset, matrix);
    this.#syncPurchaseOptionsQuantity(fieldset);
    this.#updateAddToCartTotalPrice();
  }

  #updatePurchaseOptionsLabels(fieldset, planDaysMap) {
    const usageSelect = fieldset.querySelector('[data-purchase-options-usage]');
    if (!(usageSelect instanceof HTMLSelectElement)) return;

    const piecesPerVariant = Number.parseInt(fieldset.dataset.piecesPerVariant || '0', 10);
    const planSelect = fieldset.querySelector('[data-purchase-options-plan]');
    if (!(planSelect instanceof HTMLSelectElement)) return;

    restoreStashedSelectOptions(planSelect);

    // NRO min/max constraints (rendered server-side from variant metafields).
    //   min: minimum number of pieces per cycle (variant.metafields.NRO.minimum_order_amount).
    //   max: maximum number of PACKS per cycle, derived from the matching carton's pieces.
    //   maxCartonCount: global theme-setting cap on cartons per delivery
    //                   (settings.nro_max_cartons_per_delivery), only emitted
    //                   for Carton variants.
    // Each is 0 when its underlying source isn't set — meaning no constraint.
    const nroMinPieces = Number.parseInt(fieldset.dataset.nroMinPieces || '0', 10);
    const nroMaxPacks = Number.parseInt(fieldset.dataset.nroMaxPacks || '0', 10);
    const nroMaxCartonCount = Number.parseInt(fieldset.dataset.nroMaxCartonCount || '0', 10);
    const nroMaxUsage = Number.parseInt(fieldset.dataset.nroMaxUsage || `${USAGE_HARD_CEILING}`, 10);

    const planDaysValues = Array.from(planDaysMap.values()).filter((value) => Number.isFinite(value) && value > 0);
    const maxPlanDays = planDaysValues.length ? Math.max(...planDaysValues) : 0;
    const toleranceDays = 7;

    // Cap the "pieces per day" dropdown to the range of usage values that
    // produce at least one visible plan, given the current min/max + matrix
    // of available plan days. Upper bound is data-nro-max-usage (theme setting),
    // with USAGE_HARD_CEILING as fallback.
    syncPurchaseOptionsUsageRange(usageSelect, {
      piecesPerVariant,
      planDaysValues,
      nroMinPieces,
      nroMaxPacks,
      nroMaxCartonCount,
      maxUsage: nroMaxUsage,
      toleranceDays,
    });

    const usageCount = Math.max(1, Number.parseInt(usageSelect.value || '1', 10));

    const bestOptionByCarton = new Map();
    let hasVisibleOption = false;

    const options = Array.from(planSelect.options);

    options.forEach((option) => {
      const planId = option.dataset.sellingPlanId || option.value;
      if (!planId) return;
      const planDays = planDaysMap.get(planId);
      if (!planDays || Number.isNaN(planDays) || piecesPerVariant <= 0) return;

      const cartonFloat = (planDays * usageCount) / piecesPerVariant;
      const cartonCount = Math.max(1, Math.round(cartonFloat));
      const expectedPlanDays = (cartonCount * piecesPerVariant) / usageCount;
      const dayDiff = Math.abs(planDays - expectedPlanDays);

      if (dayDiff > toleranceDays) return;

      const currentBest = bestOptionByCarton.get(cartonCount);
      if (!currentBest || dayDiff < currentBest.dayDiff) {
        bestOptionByCarton.set(cartonCount, { option, dayDiff, planDays, cartonCount, expectedPlanDays });
      }
    });

    options.forEach((option) => {
      const planId = option.dataset.sellingPlanId || option.value;
      const planName = option.dataset.planName || option.textContent || '';
      const planDays = planDaysMap.get(planId);

      if (planId && !planDaysMap.has(planId)) {
        option.hidden = true;
        return;
      }

      if (!planDays || Number.isNaN(planDays)) {
        option.hidden = false;
        option.textContent = planName;
        hasVisibleOption = true;
        return;
      }

      if (maxPlanDays && planDays > maxPlanDays) {
        option.hidden = true;
        return;
      }

      if (piecesPerVariant > 0) {
        const cartonCount = Math.max(1, Math.round((planDays * usageCount) / piecesPerVariant));
        const best = bestOptionByCarton.get(cartonCount);
        if (!best || best.option !== option) {
          option.hidden = true;
          return;
        }
        // NRO minimum: cartonCount * piecesPerVariant must reach nroMinPieces.
        if (nroMinPieces > 0 && cartonCount * piecesPerVariant < nroMinPieces) {
          option.hidden = true;
          return;
        }
        // NRO maximum: cartonCount must not exceed nroMaxPacks (carton-equivalent boundary).
        if (nroMaxPacks > 0 && cartonCount > nroMaxPacks) {
          option.hidden = true;
          return;
        }
        // Global theme-setting cap on cartons per delivery (Carton variants only).
        if (nroMaxCartonCount > 0 && cartonCount > nroMaxCartonCount) {
          option.hidden = true;
          return;
        }
        option.dataset.cartonCount = cartonCount.toString();
        const displayDays = Math.max(1, Math.floor(planDays));
        option.dataset.planDays = displayDays.toString();
        option.textContent = formatCartonPerDaysLabel(fieldset, cartonCount, displayDays);
        option.hidden = false;
        hasVisibleOption = true;
        return;
      }

      option.hidden = false;
      option.textContent = planName;
      hasVisibleOption = true;
    });

    const selectedOption = planSelect.options[planSelect.selectedIndex];
    if (selectedOption?.hidden && hasVisibleOption) {
      const firstVisibleOption = Array.from(planSelect.options).find((option) => !option.hidden);
      if (firstVisibleOption) {
        planSelect.value = firstVisibleOption.value;
      }
    }

    this.#sortPurchaseOptions(planSelect);
    detachHiddenSelectOptions(planSelect);
  }

  #sortPurchaseOptions(planSelect) {
    const options = Array.from(planSelect.options);
    const oneTimeOption = options.find((option) => option.value === '');
    const planOptions = options.filter((option) => option.value !== '' && !option.hidden);

    planOptions.sort((a, b) => {
      const aCartons = Number.parseInt(a.dataset.cartonCount || '', 10);
      const bCartons = Number.parseInt(b.dataset.cartonCount || '', 10);
      const aValue = Number.isNaN(aCartons) ? Number.MAX_SAFE_INTEGER : aCartons;
      const bValue = Number.isNaN(bCartons) ? Number.MAX_SAFE_INTEGER : bCartons;
      return aValue - bValue;
    });

    const fragment = document.createDocumentFragment();
    if (oneTimeOption) fragment.appendChild(oneTimeOption);
    planOptions.forEach((option) => fragment.appendChild(option));
    planSelect.appendChild(fragment);
  }

  #updatePurchaseOptionsDiscount(fieldset, matrix) {
    const promo = fieldset.querySelector('[data-purchase-options-promo]');
    const label = fieldset.querySelector('[data-purchase-options-discount-label]');
    const comparePrice = fieldset.querySelector('[data-purchase-options-compare-price]');
    const discountPrice = fieldset.querySelector('[data-purchase-options-discount-price]');
    const priceEach = fieldset.querySelector('[data-purchase-options-price-each]');

    if (
      !(promo instanceof HTMLElement) ||
      !(label instanceof HTMLElement) ||
      !(comparePrice instanceof HTMLElement) ||
      !(discountPrice instanceof HTMLElement)
    ) {
      return;
    }

    const planSelect = fieldset.querySelector('[data-purchase-options-plan]');
    if (!(planSelect instanceof HTMLSelectElement)) {
      promo.hidden = true;
      return;
    }

    const fallbackOption = Array.from(planSelect.options).find((option) => !option.hidden && option.value !== '');
    const planId = planSelect.value || fallbackOption?.value || '';
    const percent = this.#getPlanDiscountPercent(matrix, planId);
    const rawPrice = Number.parseInt(fieldset.dataset.variantPrice || '0', 10);

    if (!percent || Number.isNaN(rawPrice) || rawPrice <= 0) {
      promo.hidden = true;
      this.#updatePurchaseOptionsPriceEach(fieldset, priceEach, rawPrice);
      this.#updatePurchaseOptionsBenefits(fieldset, {
        percent: percent || 0,
        price: this.#formatMoneyCents(rawPrice),
      });
      return;
    }

    const discountedPrice = Math.round(rawPrice * (100 - percent) / 100);
    const template = label.dataset.discountTemplate || '[[percent]]% discount';
    label.textContent = template.replace('[[percent]]', percent.toString());
    comparePrice.textContent = this.#formatMoneyCents(rawPrice);
    discountPrice.textContent = this.#formatMoneyCents(discountedPrice);
    this.#updatePurchaseOptionsPriceEach(fieldset, priceEach, discountedPrice);
    promo.hidden = false;
    this.#updatePurchaseOptionsBenefits(fieldset, {
      percent,
      price: this.#formatMoneyCents(discountedPrice),
    });
    return;
  }

  #getPlanDiscountPercent(matrix, planId) {
    if (!matrix || !planId) return 0;
    const groups = matrix?.selling_plan_groups || [];
    for (const group of groups) {
      const sellingPlans = group?.selling_plans || [];
      for (const sellingPlan of sellingPlans) {
        if (sellingPlan?.id?.toString() !== planId.toString()) continue;
        const adjustments = sellingPlan?.price_adjustments || [];
        for (const adjustment of adjustments) {
          if (adjustment?.value_type === 'percentage') {
            const value = Number.parseFloat(adjustment?.value);
            if (Number.isFinite(value) && value > 0) return value;
          }
        }
      }
    }
    return 0;
  }

  #updatePurchaseOptionsBenefits(fieldset, { percent, price }) {
    const benefitItems = fieldset.querySelectorAll('[data-subscription-benefit]');
    if (!benefitItems.length) return;

    const percentText = String(percent);
    benefitItems.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const template = item.dataset.benefitTemplate || '';
      if (!template) return;
      const updatedText = template.replace('[price]', price).replace('[percent]', percentText);
      const textEl = item.querySelector('[data-subscription-benefit-text]');
      if (textEl) {
        textEl.textContent = updatedText;
      } else {
        item.textContent = updatedText;
      }
    });
  }

  #formatMoneyCents(moneyValue) {
    const moneyFormat = this.querySelector('[data-money-format]');
    if (!(moneyFormat instanceof HTMLTemplateElement)) return '';
    const template = moneyFormat.content.textContent || '{{amount}}';
    const placeholderMatch = template.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';

    const currency = (this.dataset.currency || '').toUpperCase();
    const decimalsMap = {
      BIF: 0,
      CLP: 0,
      DJF: 0,
      GNF: 0,
      JPY: 0,
      KMF: 0,
      KRW: 0,
      MGA: 0,
      PYG: 0,
      RWF: 0,
      UGX: 0,
      VND: 0,
      VUV: 0,
      XAF: 0,
      XOF: 0,
      XPF: 0,
    };

    const basePrecision = decimalsMap[currency] ?? 2;
    let precision = basePrecision;
    let thousandsSeparator = ',';
    let decimalSeparator = '.';

    switch (placeholder) {
      case 'amount_no_decimals':
        precision = 0;
        break;
      case 'amount_with_comma_separator':
        thousandsSeparator = '.';
        decimalSeparator = ',';
        break;
      case 'amount_no_decimals_with_comma_separator':
        precision = 0;
        thousandsSeparator = '.';
        decimalSeparator = ',';
        break;
      case 'amount_with_space_separator':
        thousandsSeparator = ' ';
        break;
      case 'amount_no_decimals_with_space_separator':
        precision = 0;
        thousandsSeparator = ' ';
        break;
      case 'amount_with_apostrophe_separator':
        thousandsSeparator = "'";
        break;
      default:
        break;
    }

    const roundedNumber = (moneyValue / 100).toFixed(precision);
    const [intPart, decPart] = roundedNumber.split('.');
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    const formattedValue = decPart ? `${formattedInt}${decimalSeparator}${decPart}` : formattedInt;

    return template.replace(placeholderMatch ? placeholderMatch[0] : '{{amount}}', formattedValue);
  }

  #updatePurchaseOptionsPriceEach(fieldset, priceEachElement, totalPriceCents) {
    if (!(priceEachElement instanceof HTMLElement)) return;
    const piecesPerVariant = Number.parseInt(fieldset.dataset.piecesPerVariant || '0', 10);
    const template = fieldset.dataset.priceEachTemplate || '/ [[price]] ea';

    if (Number.isNaN(totalPriceCents) || totalPriceCents <= 0 || piecesPerVariant <= 0) {
      return;
    }

    const eachPriceCents = totalPriceCents / piecesPerVariant;
    priceEachElement.textContent = template.replace('[[price]]', this.#formatMoneyCents(eachPriceCents));
    priceEachElement.hidden = false;
  }

  #togglePurchaseOptions(fieldset, { animate = true } = {}) {
    const toggle = fieldset.querySelector('[data-purchase-options-toggle]');
    const details = fieldset.querySelector('[data-purchase-options-details]');
    if (!(toggle instanceof HTMLInputElement) || !(details instanceof HTMLElement)) return;
    this.#setQuantitySelectorVisibility(fieldset, toggle.checked);
    const existingHandler = purchaseOptionsTransitionHandlers.get(details);
    if (existingHandler) {
      details.removeEventListener('transitionend', existingHandler);
      purchaseOptionsTransitionHandlers.delete(details);
    }

    if (!animate) {
      if (toggle.checked) {
        details.hidden = false;
        details.removeAttribute('hidden');
        details.style.opacity = '';
        details.style.maxHeight = '';
      } else {
        details.hidden = true;
        details.setAttribute('hidden', '');
        details.style.opacity = '0';
        details.style.maxHeight = '0px';
      }
      return;
    }

    details.style.transition = '';
    details.style.transitionDuration = '';

    if (toggle.checked) {
      details.hidden = false;
      details.removeAttribute('hidden');
      details.style.opacity = '0';
      details.style.maxHeight = '0px';
      void details.offsetHeight;
      const targetHeight = details.scrollHeight;
      details.style.opacity = '1';
      details.style.maxHeight = `${targetHeight}px`;
      const onTransitionEnd = () => {
        details.style.maxHeight = 'none';
        details.removeEventListener('transitionend', onTransitionEnd);
        purchaseOptionsTransitionHandlers.delete(details);
      };
      purchaseOptionsTransitionHandlers.set(details, onTransitionEnd);
      details.addEventListener('transitionend', onTransitionEnd);
    } else {
      details.style.opacity = '1';
      details.style.maxHeight = `${details.scrollHeight}px`;
      void details.offsetHeight;
      details.style.opacity = '0';
      details.style.maxHeight = '0px';
      const onTransitionEnd = () => {
        details.hidden = true;
        details.setAttribute('hidden', '');
        details.removeEventListener('transitionend', onTransitionEnd);
        purchaseOptionsTransitionHandlers.delete(details);
      };
      purchaseOptionsTransitionHandlers.set(details, onTransitionEnd);
      details.addEventListener('transitionend', onTransitionEnd);
    }

    const planSelect = fieldset.querySelector('[data-purchase-options-plan]');
    if (!(planSelect instanceof HTMLSelectElement)) return;

    if (!toggle.checked) {
      planSelect.value = '';
      planSelect.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (!planSelect.value) {
      const firstVisibleOption = Array.from(planSelect.options).find((option) => !option.hidden);
      if (firstVisibleOption) {
        planSelect.value = firstVisibleOption.value;
      }
    }

    planSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #setQuantitySelectorVisibility(fieldset, hideQuantitySelector) {
    const productFormId = fieldset.dataset.productFormId;
    const form =
      (productFormId ? document.getElementById(productFormId) : null) ||
      fieldset.closest('form') ||
      fieldset.closest('product-form-component');
    if (!(form instanceof HTMLElement)) return;

    const quantitySelectorWrapper = form.querySelector('.quantity-selector-wrapper');
    if (!(quantitySelectorWrapper instanceof HTMLElement)) return;

    quantitySelectorWrapper.classList.toggle('hidden', hideQuantitySelector);
  }

  #animatePurchaseOptionsClose() {
    const fieldset = this.querySelector('[data-purchase-options]');
    if (!(fieldset instanceof HTMLElement)) return Promise.resolve();
    const details = fieldset.querySelector('[data-purchase-options-details]');
    if (!(details instanceof HTMLElement)) return Promise.resolve();
    if (details.hidden) return Promise.resolve();

    const existingHandler = purchaseOptionsTransitionHandlers.get(details);
    if (existingHandler) {
      details.removeEventListener('transitionend', existingHandler);
      details.removeEventListener('transitioncancel', existingHandler);
      purchaseOptionsTransitionHandlers.delete(details);
    }

    const finishClose = () => {
      details.hidden = true;
      details.setAttribute('hidden', '');
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finishClose();
      return Promise.resolve();
    }

    details.style.transition = '';
    details.style.transitionDuration = PURCHASE_OPTIONS_CLOSE_DURATION;
    details.style.opacity = '1';
    details.style.maxHeight = `${details.scrollHeight}px`;
    void details.offsetHeight;
    details.style.opacity = '0';
    details.style.maxHeight = '0px';

    const transitionDuration = window.getComputedStyle(details).transitionDuration || '';
    if (transitionDuration.split(',').every((value) => parseFloat(value) === 0)) {
      finishClose();
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const onTransitionFinish = () => {
        finishClose();
        details.removeEventListener('transitionend', onTransitionFinish);
        details.removeEventListener('transitioncancel', onTransitionFinish);
        purchaseOptionsTransitionHandlers.delete(details);
        resolve(undefined);
      };
      purchaseOptionsTransitionHandlers.set(details, onTransitionFinish);
      details.addEventListener('transitionend', onTransitionFinish);
      details.addEventListener('transitioncancel', onTransitionFinish);
    });
  }

  #closePurchaseOptionsForTransition() {
    this.#animatePurchaseOptionsContainerClose();
    return this.#animatePurchaseOptionsClose();
  }

  #openPurchaseOptionsAfterTransition() {
    const fieldset = this.querySelector('[data-purchase-options]');
    if (!(fieldset instanceof HTMLElement)) return;
    if (fieldset.dataset.subscriptionEnabled !== 'true') return;

    this.#animatePurchaseOptionsContainerOpen(fieldset);
    this.#togglePurchaseOptions(fieldset, { animate: true });
  }

  #animatePurchaseOptionsContainerClose() {
    const fieldset = this.querySelector('[data-purchase-options]');
    if (!(fieldset instanceof HTMLElement)) return;
    if (fieldset.hidden) return;
    if (fieldset.dataset.purchaseOptionsHidden === 'true') return;

    const existingHandler = purchaseOptionsTransitionHandlers.get(fieldset);
    if (existingHandler) {
      fieldset.removeEventListener('transitionend', existingHandler);
      purchaseOptionsTransitionHandlers.delete(fieldset);
    }

    fieldset.style.transition = '';
    fieldset.style.transitionDuration = PURCHASE_OPTIONS_CLOSE_DURATION;
    fieldset.style.opacity = '1';
    fieldset.style.maxHeight = `${fieldset.scrollHeight}px`;
    void fieldset.offsetHeight;
    fieldset.style.opacity = '0';
    fieldset.style.maxHeight = '0px';

    const onTransitionEnd = () => {
      fieldset.hidden = true;
      fieldset.removeEventListener('transitionend', onTransitionEnd);
      purchaseOptionsTransitionHandlers.delete(fieldset);
    };
    purchaseOptionsTransitionHandlers.set(fieldset, onTransitionEnd);
    fieldset.addEventListener('transitionend', onTransitionEnd);
  }

  #animatePurchaseOptionsContainerOpen(fieldset) {
    const existingHandler = purchaseOptionsTransitionHandlers.get(fieldset);
    if (existingHandler) {
      fieldset.removeEventListener('transitionend', existingHandler);
      purchaseOptionsTransitionHandlers.delete(fieldset);
    }

    fieldset.style.transition = '';
    fieldset.style.transitionDuration = '';
    fieldset.hidden = false;
    fieldset.removeAttribute('hidden');
    fieldset.style.opacity = '0';
    fieldset.style.maxHeight = '0px';
    void fieldset.offsetHeight;
    fieldset.style.opacity = '1';
    fieldset.style.maxHeight = `${fieldset.scrollHeight}px`;

    const onTransitionEnd = () => {
      fieldset.style.maxHeight = 'none';
      fieldset.removeEventListener('transitionend', onTransitionEnd);
      purchaseOptionsTransitionHandlers.delete(fieldset);
    };
    purchaseOptionsTransitionHandlers.set(fieldset, onTransitionEnd);
    fieldset.addEventListener('transitionend', onTransitionEnd);
  }

  /**
   * Sync the hidden NRO line-item property inputs. Two cart lines with the
   * same (variant_id, selling_plan_id) but different (usage, cycle days,
   * carton count) MUST stay separate — Shopify merges by
   * (variant_id, selling_plan_id, properties), so the properties are the
   * thing that keeps them distinct.
   *
   * When NRO is OFF, all three property inputs are blanked. Shopify drops
   * empty property values from the cart payload, so one-time purchases
   * behave normally (no merging weirdness from blank-keyed properties).
   */
  #syncPurchaseOptionsProperties(fieldset) {
    if (!(fieldset instanceof HTMLElement)) return;

    const toggle = fieldset.querySelector('[data-purchase-options-toggle]');
    const requiresSellingPlan = fieldset.dataset.requiresSellingPlan === 'true';
    const isNroOn = toggle instanceof HTMLInputElement ? toggle.checked : requiresSellingPlan;

    const propUsage = fieldset.querySelector('[data-purchase-options-property="usage"]');
    const propCycle = fieldset.querySelector('[data-purchase-options-property="cycle_days"]');
    const propCount = fieldset.querySelector('[data-purchase-options-property="carton_count"]');

    if (!isNroOn) {
      if (propUsage instanceof HTMLInputElement) propUsage.value = '';
      if (propCycle instanceof HTMLInputElement) propCycle.value = '';
      if (propCount instanceof HTMLInputElement) propCount.value = '';
      return;
    }

    const usageSelect = fieldset.querySelector('[data-purchase-options-usage]');
    const planSelect = fieldset.querySelector('[data-purchase-options-plan]');

    let usageValue = '';
    if (usageSelect instanceof HTMLSelectElement) {
      usageValue = usageSelect.value || '';
    }

    let cycleDays = '';
    let cartonCount = '';
    if (planSelect instanceof HTMLSelectElement) {
      const selectedOption = planSelect.options[planSelect.selectedIndex];
      if (selectedOption) {
        cycleDays = selectedOption.dataset.planDays || '';
        cartonCount = selectedOption.dataset.cartonCount || '';
      }
    }

    if (propUsage instanceof HTMLInputElement) propUsage.value = usageValue;
    if (propCycle instanceof HTMLInputElement) propCycle.value = cycleDays;
    if (propCount instanceof HTMLInputElement) propCount.value = cartonCount;
  }

  #syncPurchaseOptionsQuantity(fieldset) {
    this.#syncPurchaseOptionsProperties(fieldset);

    const toggle = fieldset.querySelector('[data-purchase-options-toggle]');
    const requiresSellingPlan = fieldset.dataset.requiresSellingPlan === 'true';
    const isNroOn = toggle instanceof HTMLInputElement ? toggle.checked : requiresSellingPlan;
    if (!isNroOn) return;

    const planSelect = fieldset.querySelector('[data-purchase-options-plan]');
    if (!(planSelect instanceof HTMLSelectElement)) return;

    const selectedOption = planSelect.options[planSelect.selectedIndex];
    const cartonCount = Number.parseInt(selectedOption?.dataset.cartonCount || '1', 10);
    const quantityValue = Number.isNaN(cartonCount) || cartonCount <= 0 ? 1 : cartonCount;
    const form = this.#getProductFormContainer(fieldset);

    if (!(form instanceof HTMLElement)) return;

    const quantitySelector = form.querySelector('quantity-selector-component');
    if (quantitySelector && 'setValue' in quantitySelector) {
      quantitySelector.setValue(quantityValue.toString());
      if ('onQuantityChange' in quantitySelector) {
        quantitySelector.onQuantityChange();
      }
      if ('updateButtonStates' in quantitySelector) {
        quantitySelector.updateButtonStates();
      }
      this.#updateAddToCartTotalPrice();
      return;
    }

    const quantityInput = form.querySelector('input[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityValue.toString();
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.#updateAddToCartTotalPrice();
    }
  }

  #getProductFormContainer(root = this) {
    // 1) Preferred: explicit data-product-form-id on root or a descendant.
    let productFormId = root instanceof HTMLElement ? root.dataset.productFormId : '';
    if (!productFormId && root instanceof HTMLElement) {
      const carrier = root.querySelector('[data-product-form-id]');
      if (carrier instanceof HTMLElement) {
        productFormId = carrier.dataset.productFormId || '';
      }
    }
    if (productFormId) {
      const byId = document.getElementById(productFormId);
      if (byId) return byId;
    }
    // 2) Fallback: a product-form-component ANCESTOR (legacy in-form layout).
    //    We deliberately do NOT use root.closest('form') because the variant
    //    picker contains its own inner <form class="variant-picker__form">,
    //    which would steal the lookup.
    const ancestorPFC = root.closest('product-form-component');
    if (ancestorPFC) return ancestorPFC;
    // 3) Last resort: any product-form-component in the document.
    const anyPFC = document.querySelector('product-form-component');
    if (anyPFC) return anyPFC;
    return null;
  }

  #getSelectedVariantPriceCents() {
    const variantScript = this.querySelector('script[type="application/json"]:not([data-purchase-options-matrix])');
    if (!(variantScript instanceof HTMLScriptElement) || !variantScript.textContent) return null;

    try {
      const variant = JSON.parse(variantScript.textContent);
      const priceCents = Number.parseInt(String(variant?.price ?? ''), 10);
      if (Number.isNaN(priceCents) || priceCents < 0) return null;
      return priceCents;
    } catch (error) {
      console.warn('Invalid selected variant JSON while updating add-to-cart total price', error);
      return null;
    }
  }

  #getEffectiveUnitPriceCents() {
    const fallbackVariantPrice = this.#getSelectedVariantPriceCents();
    const fieldset = this.querySelector('[data-purchase-options]');
    if (!(fieldset instanceof HTMLElement)) return fallbackVariantPrice;

    const fieldsetVariantPrice = Number.parseInt(fieldset.dataset.variantPrice || '', 10);
    const basePriceCents =
      !Number.isNaN(fieldsetVariantPrice) && fieldsetVariantPrice > 0 ? fieldsetVariantPrice : fallbackVariantPrice;
    if (basePriceCents === null || basePriceCents <= 0) return null;

    const toggle = fieldset.querySelector('[data-purchase-options-toggle]');
    const requiresSellingPlan = fieldset.dataset.requiresSellingPlan === 'true';
    const isSubscriptionSelected = toggle instanceof HTMLInputElement ? toggle.checked : requiresSellingPlan;
    if (!isSubscriptionSelected) return basePriceCents;

    const planSelect = fieldset.querySelector('[data-purchase-options-plan]');
    if (!(planSelect instanceof HTMLSelectElement)) return basePriceCents;

    const fallbackOption = Array.from(planSelect.options).find((option) => !option.hidden && option.value !== '');
    const planId = planSelect.value || fallbackOption?.value || '';
    const matrix = this.#getPurchaseOptionsMatrix();
    const percent = this.#getPlanDiscountPercent(matrix, planId);
    if (!percent || percent <= 0) return basePriceCents;

    return Math.round(basePriceCents * (100 - percent) / 100);
  }

  #getCurrentQuantity(form) {
    const quantitySelector = form.querySelector('quantity-selector-component');
    if (quantitySelector && 'getValue' in quantitySelector && typeof quantitySelector.getValue === 'function') {
      const selectorValue = Number.parseInt(quantitySelector.getValue(), 10);
      if (!Number.isNaN(selectorValue) && selectorValue > 0) return selectorValue;
    }

    const quantityInput = form.querySelector('input[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement) {
      const inputValue = Number.parseInt(quantityInput.value, 10);
      if (!Number.isNaN(inputValue) && inputValue > 0) return inputValue;
    }

    return 1;
  }

  #updateAddToCartTotalPrice() {
    const form = this.#getProductFormContainer();
    if (!(form instanceof HTMLElement)) return;

    const priceElements = form.querySelectorAll('.add-to-cart-text__price');
    if (!priceElements.length) return;

    const unitPriceCents = this.#getEffectiveUnitPriceCents();
    if (unitPriceCents === null) return;

    const quantityValue = this.#getCurrentQuantity(form);
    const totalPriceText = this.#formatMoneyCents(unitPriceCents * quantityValue);

    priceElements.forEach((priceElement) => {
      priceElement.textContent = ` - ${totalPriceText}`;
    });
  }

  #setPurchaseOptionsVisibility(fieldset, { animate = true } = {}) {
    const hasSubscriptionOptions = fieldset.dataset.subscriptionEnabled === 'true';
    if (hasSubscriptionOptions) {
      fieldset.dataset.purchaseOptionsHidden = 'false';
      fieldset.hidden = false;
      fieldset.style.transition = '';
      fieldset.style.opacity = '1';
      fieldset.style.maxHeight = 'none';
      return;
    }

    if (!animate) {
      fieldset.style.transition = 'none';
    } else {
      fieldset.style.transition = '';
    }

    fieldset.dataset.purchaseOptionsHidden = 'true';
    fieldset.hidden = false;
    if (animate) {
      fieldset.style.maxHeight = `${fieldset.scrollHeight}px`;
      requestAnimationFrame(() => {
        fieldset.style.opacity = '0';
        fieldset.style.maxHeight = '0px';
      });
      const onTransitionEnd = () => {
        fieldset.hidden = true;
        fieldset.removeEventListener('transitionend', onTransitionEnd);
      };
      fieldset.addEventListener('transitionend', onTransitionEnd);
    } else {
      fieldset.style.opacity = '0';
      fieldset.style.maxHeight = '0px';
      fieldset.hidden = true;
    }
  }

  #getPurchaseOptionsMatrix() {
    const matrixScript = this.querySelector('[data-purchase-options-matrix]');
    if (!(matrixScript instanceof HTMLScriptElement)) return null;
    if (!matrixScript.textContent) return null;
    try {
      return JSON.parse(matrixScript.textContent);
    } catch (error) {
      console.warn('Invalid purchase options matrix', error);
      return null;
    }
  }

  /**
   * Ensure hidden NRO properties reflect the current UI before /cart/add.
   * Called from product-form.js on submit so cycle days are never stale.
   */
  syncPurchaseOptionsBeforeSubmit() {
    const fieldset = this.querySelector('[data-purchase-options]');
    if (fieldset instanceof HTMLElement) {
      this.#syncPurchaseOptionsQuantity(fieldset);
    }
  }

  #intervalToDays(interval, count) {
    const intervalCount = Number(count) || 0;
    if (intervalCount <= 0) return 0;

    switch (interval) {
      case 'week':
        return intervalCount * 7;
      case 'month':
        return intervalCount * 30;
      case 'year':
        return intervalCount * 365;
      default:
        return intervalCount;
    }
  }

  #getDaysFromSellingPlan(sellingPlan) {
    const deliveryPolicy = sellingPlan?.delivery_policy;
    if (deliveryPolicy?.interval_count > 0) {
      const days = this.#intervalToDays(deliveryPolicy.interval, deliveryPolicy.interval_count);
      if (days > 0) return days;
    }

    const billingPolicy = sellingPlan?.billing_policy;
    if (billingPolicy?.interval_count > 0) {
      const days = this.#intervalToDays(billingPolicy.interval, billingPolicy.interval_count);
      if (days > 0) return days;
    }

    const optionValues = sellingPlan?.options?.[0]?.values || [];
    const rawValue = optionValues[0] || sellingPlan?.name || '';
    return this.#extractDays(rawValue);
  }

  #getPlanDaysMap(matrix) {
    const planDaysMap = new Map();
    const groups = matrix?.selling_plan_groups || [];
    groups.forEach((group) => {
      const sellingPlans = group?.selling_plans || [];
      sellingPlans.forEach((sellingPlan) => {
        const planId = sellingPlan?.id?.toString();
        if (!planId) return;
        const planDays = this.#getDaysFromSellingPlan(sellingPlan);
        if (planDays) {
          planDaysMap.set(planId, planDays);
        }
      });
    });

    return planDaysMap;
  }

  #extractDays(rawValue) {
    if (!rawValue) return 0;
    const raw = rawValue.toString();
    const dayMatch = raw.match(/(\d+)\s*day/i);
    if (dayMatch) return Number.parseInt(dayMatch[1], 10);
    const match = raw.match(/(\d+)/);
    if (!match) return 0;
    return Number.parseInt(match[1], 10);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected][data-option-value-id], fieldset input[data-option-value-id]:checked'
    );

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected][data-option-value-id], fieldset input[data-option-value-id]:checked')
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}